import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MessagesSquare, Send, ArrowLeft, Loader2, BadgeCheck, Building2, MapPin, MoreVertical,
  Home, ClipboardList, Share2, Ban, Flag, ExternalLink, ShieldCheck,
  Handshake, TrendingUp, Check, PlusCircle, ChevronRight,
} from "lucide-react";

const REPORT_REASONS = [
  ["SPAM", "Spam"], ["HARASSMENT", "Harassment"], ["INAPPROPRIATE", "Inappropriate content"],
  ["SCAM", "Scam / fraud"], ["OTHER", "Other"],
];

function Avatar({ name, url, size = "h-10 w-10" }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return url
    ? <img src={url} alt={name} className={`${size} rounded-full object-cover shrink-0`} />
    : <div className={`${size} rounded-full bg-[#0F2C59] text-white grid place-items-center font-semibold shrink-0`}>{initials}</div>;
}

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const activeId = params.get("conversation");
  const [convos, setConvos] = useState(null);

  const loadInbox = useCallback(() => {
    api.get("/messages/conversations").then((r) => setConvos(r.data.items)).catch(() => setConvos([]));
  }, []);
  useEffect(() => { trackEvent("connections_page_viewed", { path: "/messages" }); }, []);
  useEffect(() => { loadInbox(); }, [loadInbox, activeId]);

  const open = (id) => setParams({ conversation: id });
  const back = () => setParams({});

  return (
    <PageShell testid="messages-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0F2C59] flex items-center gap-2">
          <MessagesSquare className="h-7 w-7 text-[#C79A3B]" /> Messages
        </h1>
        <p className="mt-1 text-slate-500 text-sm">Private, business-oriented conversations with your accepted connections.</p>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-4 min-h-[60vh]">
        {/* Inbox — hidden on mobile when a conversation is open */}
        <div className={`${activeId ? "hidden lg:block" : "block"} rounded-2xl border border-slate-200 bg-white overflow-hidden`}>
          <InboxList convos={convos} activeId={activeId} onOpen={open} />
        </div>
        {/* Active conversation — hidden on mobile when none selected */}
        <div className={`${activeId ? "block" : "hidden lg:block"} rounded-2xl border border-slate-200 bg-white overflow-hidden`}>
          {activeId
            ? <Conversation key={activeId} cid={activeId} onBack={back} onChanged={loadInbox} />
            : <div className="h-full grid place-items-center text-slate-400 py-24 text-sm">Select a conversation to start messaging</div>}
        </div>
      </div>
    </PageShell>
  );
}

function InboxList({ convos, activeId, onOpen }) {
  if (!convos) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!convos.length) return <div className="p-8 text-center text-sm text-slate-500" data-testid="inbox-empty">No conversations yet. Open a connection and tap <span className="font-medium text-[#0F2C59]">Message</span> to start.</div>;
  return (
    <div className="divide-y divide-slate-100" data-testid="inbox-list">
      {convos.map((c) => (
        <button key={c.conversation_id} data-testid={`inbox-item-${c.conversation_id}`} onClick={() => onOpen(c.conversation_id)}
          className={`w-full flex items-center gap-3 p-3.5 text-left hover:bg-slate-50 transition-colors ${activeId === c.conversation_id ? "bg-slate-50" : ""}`}>
          <Avatar name={c.participant.name} url={c.participant.avatar_url} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-semibold text-[#0F2C59] truncate">{c.participant.name}</span>
              {c.participant.verified && <BadgeCheck className="h-4 w-4 text-success shrink-0" />}
            </div>
            {c.participant.role_label && <div className="text-[11px] text-slate-400 truncate">{c.participant.role_label}</div>}
            <div className="text-[13px] text-slate-500 truncate">{c.last_message_preview}</div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-[10px] text-slate-400">{fmtTime(c.last_message_at)}</span>
            {c.unread > 0 && <span data-testid={`inbox-unread-${c.conversation_id}`} className="min-w-5 h-5 px-1.5 rounded-full bg-[#C79A3B] text-white text-[11px] font-bold grid place-items-center">{c.unread}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function Conversation({ cid, onBack, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [collabTarget, setCollabTarget] = useState(null); // { entityType, entityId }
  const endRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/messages/conversations/${cid}`).then((r) => { setData(r.data); onChanged?.(); }).catch((e) => toast.error(apiError(e)));
  }, [cid, onChanged]);
  useEffect(() => { load(); }, [cid]); // eslint-disable-line
  // lightweight poll for new messages (no websockets in Phase 1)
  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/messages/conversations/${cid}`).then((r) => setData(r.data)).catch(() => {});
    }, 12000);
    return () => clearInterval(t);
  }, [cid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/conversations/${cid}/messages`, { message_type: "TEXT", text: t });
      setText("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const doBlock = async () => {
    try { await api.post(`/messages/conversations/${cid}/${data.blocked_by_me ? "unblock" : "block"}`); toast.success(data.blocked_by_me ? "Unblocked" : "Blocked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const me = data.messages.length ? null : null; // sender identity is per-message from server
  const p = data.participant;

  return (
    <div className="flex flex-col h-full max-h-[72vh]" data-testid="conversation">
      {/* header */}
      <div className="flex items-center gap-3 p-3 border-b border-slate-100">
        <button onClick={onBack} className="lg:hidden p-1.5 -ml-1 text-slate-500 hover:text-[#0F2C59]" data-testid="conv-back"><ArrowLeft className="h-5 w-5" /></button>
        <Avatar name={p.name} url={p.avatar_url} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0"><span className="font-semibold text-[#0F2C59] truncate">{p.name}</span>{p.verified && <BadgeCheck className="h-4 w-4 text-success shrink-0" />}</div>
          <div className="text-[11px] text-slate-400 truncate">
            {[p.role_label, p.sales_position_label, p.organization].filter(Boolean).join(" · ")}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button data-testid="conv-menu" className="p-2 text-slate-400 hover:text-[#0F2C59]"><MoreVertical className="h-5 w-5" /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/connections`)}><ShieldCheck className="h-4 w-4 mr-2" /> View Profile</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="conv-block" onClick={doBlock}><Ban className="h-4 w-4 mr-2" /> {data.blocked_by_me ? "Unblock" : "Block"}</DropdownMenuItem>
            <DropdownMenuItem data-testid="conv-report" onClick={() => setReportOpen(true)}><Flag className="h-4 w-4 mr-2" /> Report</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/40" data-testid="conv-body">
        {data.messages.length === 0 && <div className="text-center text-sm text-slate-400 py-10">No messages yet. Say hello or share an opportunity.</div>}
        {data.messages.map((m) => <MessageBubble key={m.id} m={m} selfId={data.me} navigate={navigate} onCollaborate={setCollabTarget} />)}
        <div ref={endRef} />
      </div>

      {/* composer */}
      {data.can_message ? (
        <div className="p-3 border-t border-slate-100 flex items-center gap-2">
          <Button data-testid="share-opportunity-btn" variant="outline" onClick={() => setShareOpen(true)} className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59] shrink-0 px-3"><Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Share</span></Button>
          <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Write a message…" maxLength={2000} className="flex-1" />
          <Button data-testid="send-btn" onClick={send} disabled={sending || !text.trim()} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white shrink-0 px-3">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </div>
      ) : (
        <div className="p-3 border-t border-slate-100 text-center text-sm text-slate-500" data-testid="conv-disabled">
          {data.blocked_by_me ? "You've blocked this professional. Unblock to resume messaging." : "Messaging is unavailable for this conversation."}
        </div>
      )}

      <ShareOpportunityDialog open={shareOpen} onOpenChange={setShareOpen} cid={cid} onSent={load} />
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} cid={cid} />
      <CollaborateDialog target={collabTarget} onClose={() => setCollabTarget(null)} navigate={navigate} />
    </div>
  );
}

function MessageBubble({ m, selfId, navigate, onCollaborate }) {
  const mine = m.sender_id === selfId;
  return (
    <div data-testid={`msg-${m.id}`} data-sender={m.sender_id} className="flex" style={{ justifyContent: mine ? "flex-end" : "flex-start" }}>
      <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${mine ? "bg-[#0F2C59] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
        {m.message_type === "TEXT" && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
        {m.message_type === "PROPERTY_SHARE" && <OpportunityCard card={m.card} kind="property" entityId={m.entity_id} navigate={navigate} onCollaborate={onCollaborate} />}
        {m.message_type === "BUYER_REQUIREMENT_SHARE" && <OpportunityCard card={m.card} kind="buyer" entityId={m.entity_id} navigate={navigate} onCollaborate={onCollaborate} />}
        <div className={`text-[10px] mt-1 text-right ${mine ? "text-white/60" : "text-slate-400"}`}>{fmtTime(m.created_at)}</div>
      </div>
    </div>
  );
}

function OpportunityCard({ card, kind, entityId, navigate, onCollaborate }) {
  if (!card || card.unavailable) return <div className="text-[13px] text-slate-400 italic">This opportunity is no longer available.</div>;
  const peso = (n) => (typeof n === "number" ? `₱${n.toLocaleString()}` : n);
  const entityType = kind === "property" ? "property" : "buyer_requirement";
  return (
    <div data-testid={`opp-card-${kind}`} className="w-64 sm:w-72 rounded-xl overflow-hidden border border-slate-200 bg-white">
      {kind === "property" ? (
        <>
          {card.image && <img src={card.image} alt={card.title} className="w-full h-32 object-cover" />}
          <div className="p-3">
            <div className="flex items-center gap-1.5 text-[11px] text-[#C79A3B] font-semibold uppercase tracking-wide"><Home className="h-3.5 w-3.5" /> {card.listing_type || "Property"}</div>
            <div className="font-semibold text-[#0F2C59] mt-1 line-clamp-2">{card.title}</div>
            <div className="text-[13px] text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{[card.city, card.project].filter(Boolean).join(" · ") || "—"}</span></div>
            {card.price != null && <div className="text-[#0F2C59] font-bold mt-1">{peso(card.price)}</div>}
          </div>
        </>
      ) : (
        <div className="p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-[#C79A3B] font-semibold uppercase tracking-wide"><ClipboardList className="h-3.5 w-3.5" /> Buyer Need</div>
          <div className="font-semibold text-[#0F2C59] mt-1 line-clamp-2">{card.title}</div>
          {card.location && <div className="text-[13px] text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{card.location}</span></div>}
          {card.property_type && <div className="text-[13px] text-slate-500 mt-0.5">{card.property_type}</div>}
          {card.budget && <div className="text-[#0F2C59] font-bold mt-1">{peso(card.budget)}</div>}
        </div>
      )}
      <div className="grid grid-cols-2 border-t border-slate-100 divide-x divide-slate-100">
        <button data-testid={`opp-cta-${kind}`} onClick={() => navigate(card.cta)} className="py-2 text-[13px] font-semibold text-[#0F2C59] hover:bg-slate-50 flex items-center justify-center gap-1.5">
          <ExternalLink className="h-3.5 w-3.5" /> View
        </button>
        {entityId && onCollaborate && (
          <button data-testid={`opp-collaborate-${kind}`} onClick={() => onCollaborate({ entityType, entityId })} className="py-2 text-[13px] font-semibold text-[#C79A3B] hover:bg-[#C79A3B]/10 flex items-center justify-center gap-1.5">
            <Handshake className="h-3.5 w-3.5" /> Collaborate
          </button>
        )}
      </div>
    </div>
  );
}

function ShareOpportunityDialog({ open, onOpenChange, cid, onSent }) {
  const [tab, setTab] = useState("property");
  const [props, setProps] = useState(null);
  const [buyers, setBuyers] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/properties").then((r) => setProps(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => setProps([]));
    api.get("/me/buyer-requirements").then((r) => setBuyers(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => setBuyers([]));
  }, [open]);

  const share = async (message_type, entity_id) => {
    setBusy(true);
    try { await api.post(`/messages/conversations/${cid}/messages`, { message_type, entity_id }); onOpenChange(false); onSent?.(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog" className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-[#C79A3B]" /> Share Opportunity</DialogTitle>
          <DialogDescription>Send a property or Buyer Need to this connection.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button variant={tab === "property" ? "default" : "outline"} onClick={() => setTab("property")} data-testid="share-tab-property" className={`flex-1 rounded-xl ${tab === "property" ? "bg-[#0F2C59]" : ""}`}><Home className="h-4 w-4 mr-1.5" /> Property</Button>
          <Button variant={tab === "buyer" ? "default" : "outline"} onClick={() => setTab("buyer")} data-testid="share-tab-buyer" className={`flex-1 rounded-xl ${tab === "buyer" ? "bg-[#0F2C59]" : ""}`}><ClipboardList className="h-4 w-4 mr-1.5" /> Buyer Need</Button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-2">
          {tab === "property" ? (
            props === null ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6 text-slate-400" />
              : props.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No properties to share.</p>
                : props.map((p) => (
                  <button key={p.id} data-testid={`share-property-${p.id}`} disabled={busy} onClick={() => share("PROPERTY_SHARE", p.id)} className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-2.5 hover:border-[#0F2C59] text-left min-w-0">
                    <Home className="h-4 w-4 text-[#C79A3B] shrink-0" />
                    <span className="min-w-0 flex-1"><span className="block font-medium text-[#0F2C59] truncate">{p.title}</span><span className="block text-[12px] text-slate-500 truncate">{p.city || "—"}</span></span>
                  </button>
                ))
          ) : (
            buyers === null ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6 text-slate-400" />
              : buyers.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No Buyer Needs to share.</p>
                : buyers.map((b) => (
                  <button key={b.id} data-testid={`share-buyer-${b.id}`} disabled={busy} onClick={() => share("BUYER_REQUIREMENT_SHARE", b.id)} className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-2.5 hover:border-[#0F2C59] text-left min-w-0">
                    <ClipboardList className="h-4 w-4 text-[#C79A3B] shrink-0" />
                    <span className="min-w-0 flex-1"><span className="block font-medium text-[#0F2C59] truncate">{b.title || b.property_type || "Buyer Need"}</span><span className="block text-[12px] text-slate-500 truncate">{b.preferred_location || b.property_type || "—"}</span></span>
                  </button>
                ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ open, onOpenChange, cid }) {
  const [reason, setReason] = useState("SPAM");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/messages/conversations/${cid}/report`, { reason, description: desc }); toast.success("Report submitted. Our team will review it."); onOpenChange(false); setDesc(""); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog" className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag className="h-5 w-5 text-[#C79A3B]" /> Report Conversation</DialogTitle>
          <DialogDescription>Reports are confidential and reviewed by our team.</DialogDescription>
        </DialogHeader>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger data-testid="report-reason"><SelectValue /></SelectTrigger>
          <SelectContent>{REPORT_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="report-desc" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={500} placeholder="Optional details" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button data-testid="report-submit" onClick={submit} disabled={busy} className="rounded-xl bg-[#0F2C59] text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const peso = (n) => (typeof n === "number" ? `₱${n.toLocaleString()}` : n);
const budgetLabel = (o) => {
  if (o.budget_min != null && o.budget_max != null) return `${peso(o.budget_min)} – ${peso(o.budget_max)}`;
  if (o.budget_max != null) return `Up to ${peso(o.budget_max)}`;
  if (o.budget_min != null) return `From ${peso(o.budget_min)}`;
  return null;
};

// Compact display of an opportunity (property or buyer requirement) inside the pairing modal.
function OppSummary({ o, muted }) {
  const isProp = o.kind === "property";
  const price = isProp ? (o.price != null ? peso(o.price) : null) : budgetLabel(o);
  return (
    <div className={`flex items-center gap-3 min-w-0 ${muted ? "opacity-90" : ""}`}>
      <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${isProp ? "bg-[#0F2C59]/10 text-[#0F2C59]" : "bg-[#C79A3B]/15 text-[#C79A3B]"}`}>
        {isProp ? <Home className="h-4.5 w-4.5" /> : <ClipboardList className="h-4.5 w-4.5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[#0F2C59] text-sm truncate">{o.title || (isProp ? "Property" : "Buyer Need")}</div>
        <div className="text-[12px] text-slate-500 truncate">{[o.location, o.property_type, price].filter(Boolean).join(" · ") || "—"}</div>
      </div>
    </div>
  );
}

function CollaborateDialog({ target, onClose, navigate }) {
  const open = !!target;
  const [loading, setLoading] = useState(false);
  const [fixed, setFixed] = useState(null);
  const [cands, setCands] = useState([]);
  const [empty, setEmpty] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const fetchCandidates = useCallback(() => {
    if (!target) return;
    setLoading(true);
    api.get(`/collab-bridge/candidates`, { params: { entity_type: target.entityType, entity_id: target.entityId } })
      .then((r) => { setFixed(r.data.fixed); setCands(r.data.candidates || []); setEmpty(!!r.data.empty); })
      .catch((e) => { toast.error(apiError(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [target, onClose]);

  useEffect(() => { if (open) { setFixed(null); setCands([]); setEmpty(false); fetchCandidates(); } }, [open]); // eslint-disable-line

  const sharedIsProperty = fixed?.kind === "property";
  const complementNoun = sharedIsProperty ? "Buyer Need" : "property";

  const sendRequest = async (cand) => {
    if (!fixed) return;
    const property_id = sharedIsProperty ? fixed.id : cand.id;
    const buyer_requirement_id = sharedIsProperty ? cand.id : fixed.id;
    setSendingId(cand.id);
    try {
      const r = await api.post("/collaborations", { property_id, buyer_requirement_id, source: "messages" });
      toast.success("Collaboration request sent.");
      // refresh states inline so the row reflects the authoritative pair
      fetchCandidates();
      if (r.data?.id) {
        // brief delay handled by user click on View; keep them in context
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingId(null); }
  };

  const goDetail = (cand) => navigate(cand.collaboration_id ? `/collaboration/${cand.collaboration_id}` : "/collaborations");

  const renderAction = (cand) => {
    const busy = sendingId === cand.id;
    switch (cand.state) {
      case "REQUEST_SENT":
        return <span data-testid={`collab-state-${cand.id}`} className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-500"><Check className="h-3.5 w-3.5" /> Request sent</span>;
      case "REVIEW_REQUEST":
        return <Button data-testid={`collab-view-${cand.id}`} size="sm" onClick={() => goDetail(cand)} className="rounded-lg h-8 bg-[#C79A3B] hover:bg-[#b3862f] text-white text-[12px]">Review request <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button>;
      case "VIEW":
        return <Button data-testid={`collab-view-${cand.id}`} size="sm" variant="outline" onClick={() => goDetail(cand)} className="rounded-lg h-8 border-[#0F2C59]/30 text-[#0F2C59] text-[12px]">View collaboration <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button>;
      case "DECLINED": case "CANCELLED": case "CLOSED":
        return <button data-testid={`collab-view-${cand.id}`} onClick={() => goDetail(cand)} className="text-[12px] font-medium text-slate-400 hover:text-[#0F2C59] underline underline-offset-2">Closed · view</button>;
      default: // NONE
        return <Button data-testid={`collab-send-${cand.id}`} size="sm" disabled={busy} onClick={() => sendRequest(cand)} className="rounded-lg h-8 bg-[#0F2C59] hover:bg-[#143163] text-white text-[12px]">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Handshake className="h-3.5 w-3.5 mr-1" /> Collaborate</>}</Button>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="collaborate-dialog" className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Handshake className="h-5 w-5 text-[#C79A3B]" /> Start a Collaboration</DialogTitle>
          <DialogDescription>Pair this shared opportunity with one of yours to send a co-broke request.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            {fixed && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="collab-fixed">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Shared opportunity</div>
                <OppSummary o={fixed} />
              </div>
            )}

            {empty ? (
              <div className="text-center py-6 px-2" data-testid="collab-empty">
                <div className="mx-auto h-12 w-12 rounded-full bg-[#C79A3B]/15 grid place-items-center mb-3"><PlusCircle className="h-6 w-6 text-[#C79A3B]" /></div>
                <p className="text-sm text-slate-600">You don't have a {complementNoun} to pair with this yet. Add one to start collaborating.</p>
                <Button data-testid="collab-empty-cta" onClick={() => navigate(sharedIsProperty ? "/have-buyer" : "/have-property")} className="mt-4 rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white">
                  <PlusCircle className="h-4 w-4 mr-1.5" /> {sharedIsProperty ? "Post Buyer Need" : "Add a Property"}
                </Button>
              </div>
            ) : (
              <>
                <div className="text-[12px] text-slate-500">Select your {complementNoun} to pair:</div>
                <div className="max-h-72 overflow-y-auto space-y-2 -mx-1 px-1" data-testid="collab-candidates">
                  {cands.map((c) => (
                    <div key={c.id} data-testid={`collab-candidate-${c.id}`} className="rounded-xl border border-slate-200 p-2.5 flex items-center gap-3 min-w-0">
                      <div className="min-w-0 flex-1"><OppSummary o={c} /></div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {typeof c.match_score === "number" && c.match_score > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-success"><TrendingUp className="h-3 w-3" /> {c.match_score}%</span>
                        )}
                        {renderAction(c)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
